import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import ISale from '../../../app/_interfaces/ISale';
import IProduct from '../../../app/_interfaces/IProduct';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SalesService {
  constructor(private http: HttpClient) {}

  private get apiBaseUrl(): string {
    return `${environment.api.protocol}://${environment.api.host}:${environment.api.port}`;
  }

  private toProductImageUrl(
    customers_id: number,
    productId: number,
    version?: string,
  ): string {
    const cacheBuster = `${version || Date.now()}`;
    return `${this.apiBaseUrl}/api/v1/public/customers/${customers_id}/products/${productId}/image?v=${encodeURIComponent(cacheBuster)}`;
  }

  private decorateSaleProduct(
    customers_id: number,
    product?: IProduct,
  ): IProduct | undefined {
    const productId = +(product?.products_id || product?.id || 0);
    const rawImageUrl = `${product?.image_url || ''}`.trim();

    if (!productId || !rawImageUrl) {
      return product;
    }

    const version =
      `${product?.updatedAt || ''}`.trim() ||
      `${(product as any)?.updated_at || ''}`.trim() ||
      rawImageUrl;

    return {
      ...product,
      image_url: this.toProductImageUrl(customers_id, productId, version),
    };
  }

  private decorateSales(customers_id: number, sales: ISale[]): ISale[] {
    return (sales || []).map((sale) => ({
      ...sale,
      product: this.decorateSaleProduct(customers_id, sale?.product),
    }));
  }

  create(customers_id: number, sites_id: number, sale: ISale) {
    return this.http.post(
      `${environment.api.protocol}://${environment.api.host}:${environment.api.port}/api/v1/customers/${customers_id}/sites/${sites_id}/sales`,
      sale
    );
  }

  update(
    customers_id: number,
    sites_id: number,
    sales_id: number,
    sale: ISale
  ) {
    return this.http.patch(
      `${environment.api.protocol}://${environment.api.host}:${environment.api.port}/api/v1/customers/${customers_id}/sites/${sites_id}/sales/${sales_id}`,
      sale
    );
  }

  delete(customers_id: number, sites_id: number, sales_id: number) {
    return this.http.delete(
      `${environment.api.protocol}://${environment.api.host}:${environment.api.port}/api/v1/customers/${customers_id}/sites/${sites_id}/sales/${sales_id}`
    );
  }

  findByPatient(
    customers_id: number,
    patients_id: number
  ): Observable<ISale[]> {
    return this.http.get<ISale[]>(
      `${environment.api.protocol}://${environment.api.host}:${environment.api.port}/api/v1/customers/${customers_id}/patients/${patients_id}/sales`
    ).pipe(map((sales) => this.decorateSales(customers_id, sales)));
  }

  findBySite(
    customers_id: number,
    sites_id: number,
    options?: { start?: string; end?: string }
  ): Observable<ISale[]> {
    return this.http.get<ISale[]>(
      `${environment.api.protocol}://${environment.api.host}:${environment.api.port}/api/v1/customers/${customers_id}/sites/${sites_id}/sales`,
      {
        params: options,
      }
    ).pipe(map((sales) => this.decorateSales(customers_id, sales)));
  }
}
